import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import Lightbox from './Lightbox';

interface FeatureShowcaseProps {
  features: {
    id: string;
    title: {
      en: string;
      hu: string;
    };
    description: {
      en: string;
      hu: string;
    };
    imageUrl: string;
    altText: {
      en: string;
      hu: string;
    };
    isPortrait?: boolean;
  }[];
}

const FeatureShowcase: React.FC<FeatureShowcaseProps> = ({ features }) => {
  const { language } = useLanguage();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState({ url: '', alt: '' });
  
  const openLightbox = (imageUrl: string, altText: string) => {
    setSelectedImage({ url: imageUrl, alt: altText });
    setLightboxOpen(true);
  };
  
  const closeLightbox = () => {
    setLightboxOpen(false);
  };
  
  return (
    <div className="py-12 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="space-y-32">
          {features.map((feature, index) => (
            <div 
              key={feature.id}
              className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-16`}
            >
              {/* Text Content */}
              <div className="flex-1 space-y-6">
                <div className="inline-block px-3 py-1 text-sm font-semibold bg-indigo-100 text-indigo-800 rounded-full">
                  {index + 1}.
                </div>
                <h2 className="text-3xl font-bold text-gray-900 tracking-tight">
                  {language === 'en' ? feature.title.en : feature.title.hu}
                </h2>
                <p className="text-lg text-gray-500">
                  {language === 'en' ? feature.description.en : feature.description.hu}
                </p>
              </div>
              
              {/* Screenshot */}
              <div className={`flex-1 w-full ${feature.isPortrait ? 'lg:w-2/3 mx-auto' : ''}`}>
                <div 
                  className="relative rounded-2xl overflow-hidden shadow-xl transform transition-all duration-500 hover:scale-105 cursor-zoom-in"
                  onClick={() => openLightbox(
                    feature.imageUrl, 
                    language === 'en' ? feature.altText.en : feature.altText.hu
                  )}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-indigo-100 to-purple-100 opacity-50"></div>
                  <img 
                    src={feature.imageUrl} 
                    alt={language === 'en' ? feature.altText.en : feature.altText.hu}
                    className={`relative z-10 w-full h-auto rounded-2xl ${feature.isPortrait ? 'max-h-[650px] object-contain mx-auto' : ''}`} 
                  />
                  
                  {/* Browser-like frame */}
                  <div className="absolute top-0 left-0 right-0 h-8 bg-gray-100 rounded-t-2xl flex items-center px-4 z-20">
                    <div className="flex space-x-2">
                      <div className="h-3 w-3 rounded-full bg-red-500"></div>
                      <div className="h-3 w-3 rounded-full bg-yellow-500"></div>
                      <div className="h-3 w-3 rounded-full bg-green-500"></div>
                    </div>
                  </div>
                  
                  {/* Magnify indicator */}
                  <div className="absolute bottom-3 right-3 bg-white bg-opacity-80 rounded-full p-2 z-20 hover:bg-opacity-100 transition-all shadow-md">
                    <svg className="h-5 w-5 text-gray-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m4-3h-6" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Lightbox for enlarged images */}
      <Lightbox 
        imageUrl={selectedImage.url}
        altText={selectedImage.alt}
        isOpen={lightboxOpen}
        onClose={closeLightbox}
      />
    </div>
  );
};

export default FeatureShowcase; 